import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Music, Plus, Trash2, Star, GripVertical, ExternalLink, Image, Save, Loader2, ArrowLeft, Upload
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LifetunesManager = () => {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', subtitle: '', image_url: '', link_url: 'https://lifetunes.live', featured: false });

  const [dragging, setDragging] = useState(false);

  const fetchTracks = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTracks(data.tracks || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST', credentials: 'include', body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, image_url: data.url }));
      }
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) uploadFile(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const handleCreate = async () => {
    if (!form.title) return;
    setSaving('new');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, order: tracks.length })
      });
      if (res.ok) {
        setForm({ title: '', subtitle: '', image_url: '', link_url: 'https://lifetunes.live', featured: false });
        setShowForm(false);
        fetchTracks();
      }
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const handleToggleFeatured = async (track) => {
    setSaving(track.track_id);
    try {
      await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks/${track.track_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featured: !track.featured })
      });
      fetchTracks();
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Delete this track?')) return;
    setSaving(trackId);
    try {
      await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks/${trackId}`, {
        method: 'DELETE', credentials: 'include'
      });
      fetchTracks();
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Music className="w-6 h-6 text-teal-400" /> Lifetunes Manager
              </h1>
              <p className="text-gray-500 text-sm">Manage tracks that appear in the Student Portal</p>
            </div>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-teal-600 hover:bg-teal-500" data-testid="add-track-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Track
          </Button>
        </div>

        {/* Add Track Form */}
        {showForm && (
          <Card className="bg-gray-900 border-gray-800 mb-6" data-testid="add-track-form">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-white font-semibold">New Track</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Title *</label>
                  <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Morning Clarity" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="track-title-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Subtitle / Description</label>
                  <Input value={form.subtitle} onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))}
                    placeholder="e.g. 432 Hz - Natural Harmony" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="track-subtitle-input" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Track Link URL *</label>
                <Input value={form.link_url} onChange={e => setForm(p => ({ ...p, link_url: e.target.value }))}
                  placeholder="https://lifetunes.live/track/..." className="bg-gray-800 border-gray-700 text-white"
                  data-testid="track-link-input" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Cover Image</label>
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => document.getElementById('track-img-input').click()}
                  className={`relative rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
                    dragging
                      ? 'border-teal-400 bg-teal-500/10'
                      : form.image_url
                        ? 'border-gray-700 bg-gray-800/30'
                        : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                  }`}
                  data-testid="track-image-dropzone"
                >
                  <input id="track-img-input" type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="track-image-upload" />
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2 py-2">
                      <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                      <p className="text-sm text-teal-400">Uploading...</p>
                    </div>
                  ) : form.image_url ? (
                    <div className="flex items-center gap-4">
                      <img src={form.image_url} alt="cover" className="w-20 h-20 rounded-lg object-cover" />
                      <div className="text-left flex-1">
                        <p className="text-sm text-white font-medium">Image uploaded</p>
                        <p className="text-xs text-gray-500 mt-1">Drop a new image to replace</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-2">
                      <Upload className="w-8 h-8 text-gray-500" />
                      <p className="text-sm text-gray-400">Drag & drop image here</p>
                      <p className="text-xs text-gray-600">or click to browse</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.featured} onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-teal-500" data-testid="track-featured-checkbox" />
                <span className="text-sm text-gray-300">Featured track (shown prominently at top)</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} disabled={!form.title || saving === 'new'}
                  className="bg-teal-600 hover:bg-teal-500" data-testid="save-track-btn">
                  {saving === 'new' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Track
                </Button>
                <Button variant="outline" onClick={() => setShowForm(false)} className="border-gray-700 text-gray-300">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Track List */}
        {tracks.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-12 text-center">
              <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 mb-2">No tracks yet</p>
              <p className="text-gray-600 text-sm">Add tracks that will appear in the Student Portal Lifetunes section</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {tracks.map((track) => (
              <div key={track.track_id}
                className="flex items-center gap-3 p-3 bg-gray-900 border border-gray-800 rounded-xl hover:border-gray-700 transition-colors"
                data-testid={`track-item-${track.track_id}`}>
                <GripVertical className="w-4 h-4 text-gray-600 shrink-0" />

                {/* Image */}
                {track.image_url ? (
                  <img src={track.image_url} alt={track.title} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
                    <Music className="w-5 h-5 text-teal-400" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{track.title}</p>
                  {track.subtitle && <p className="text-gray-500 text-xs truncate">{track.subtitle}</p>}
                  <a href={track.link_url} target="_blank" rel="noopener noreferrer"
                    className="text-teal-400 text-xs hover:underline flex items-center gap-1 mt-0.5">
                    {track.link_url?.substring(0, 40)}{track.link_url?.length > 40 ? '...' : ''}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Actions */}
                <button onClick={() => handleToggleFeatured(track)}
                  className={`p-2 rounded-lg transition-colors ${track.featured ? 'bg-amber-500/10 text-amber-400' : 'text-gray-600 hover:text-gray-400'}`}
                  title={track.featured ? 'Remove featured' : 'Set as featured'}
                  data-testid={`toggle-featured-${track.track_id}`}>
                  <Star className={`w-4 h-4 ${track.featured ? 'fill-amber-400' : ''}`} />
                </button>
                <button onClick={() => handleDelete(track.track_id)}
                  className="p-2 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  data-testid={`delete-track-${track.track_id}`}>
                  {saving === track.track_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LifetunesManager;
